/**
 * Product analytics for the authenticated app.
 * Fire-and-forget inserts into public.app_events.
 *
 * Events:
 *   - app_route_viewed     ({ route, authenticated, time_since_last_visit_ms })
 *   - session_expired_redirect ({ from_route })
 *   - cta_clicked          ({ name, route, ...props })
 *   - time_on_route        ({ route, duration_ms })
 */
import { supabase } from "@/integrations/supabase/client";
import { recordSecurityEvent, classifySupabaseError } from "@/lib/security-events";
import { visitorClassMetadata } from "@/lib/bot-detect";


const SID_KEY = "mr_app_sid";
const LAST_VISIT_KEY = "mr_last_route_visit";

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function getSessionId(): string {
  if (typeof window === "undefined") return uuid();
  try {
    let id = localStorage.getItem(SID_KEY);
    if (!id) { id = uuid(); localStorage.setItem(SID_KEY, id); }
    return id;
  } catch { return uuid(); }
}

type Ctx = { userId?: string | null; orgId?: string | null; route?: string | null };

/**
 * Pending events are buffered and written in one batched insert while the
 * browser is idle, so navigation never waits on analytics traffic. Nothing is
 * dropped: the buffer flushes on a timer, when it fills, and on page hide.
 */
type PendingEvent = Record<string, unknown> & { event: string; organization_id: string | null };
const queue: PendingEvent[] = [];
let flushTimer: number | null = null;
const MAX_BUFFER = 20;
const FLUSH_MS = 3_000;

function sendBatch(batch: PendingEvent[]) {
  if (batch.length === 0) return;
  void supabase
    .from("app_events" as never)
    .insert(batch as never)
    .then(({ error }) => {
      if (!error) return;
      console.warn("[app-analytics]", batch.map((b) => b.event).join(","), error.message);
      const kind = classifySupabaseError(error);
      if (kind) {
        recordSecurityEvent({
          event_type: "app_events_blocked",
          severity: "warning",
          organization_id: batch[0]?.organization_id ?? null,
          source: "app_events",
          route: (batch[0]?.route as string | null) ?? null,
          metadata: { attempted_event: batch[0]?.event, reason: error.message },
        });
      }
    });
}

function flushQueue() {
  if (flushTimer !== null) {
    window.clearTimeout(flushTimer);
    flushTimer = null;
  }
  sendBatch(queue.splice(0, queue.length));
}

function scheduleFlush() {
  if (queue.length >= MAX_BUFFER) {
    flushQueue();
    return;
  }
  if (flushTimer !== null) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    const idle = (window as unknown as { requestIdleCallback?: (cb: () => void) => void })
      .requestIdleCallback;
    if (idle) idle(() => flushQueue());
    else flushQueue();
  }, FLUSH_MS);
}

let lifecycleBound = false;
function bindLifecycle() {
  if (lifecycleBound || typeof window === "undefined") return;
  lifecycleBound = true;
  window.addEventListener("pagehide", flushQueue);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushQueue();
  });
}

export function trackAppEvent(
  event: string,
  metadata: Record<string, unknown> = {},
  ctx: Ctx = {},
) {
  if (typeof window === "undefined") return;
  bindLifecycle();
  queue.push({
    event,
    session_id: getSessionId(),
    user_id: ctx.userId ?? null,
    organization_id: ctx.orgId ?? null,
    route: ctx.route ?? (typeof location !== "undefined" ? location.pathname : null),
    metadata: { ...visitorClassMetadata(), ...metadata },
  });
  scheduleFlush();
}

export function trackRouteView(route: string, ctx: Ctx = {}) {
  if (typeof window === "undefined") return;
  let timeSince: number | null = null;
  try {
    const prev = localStorage.getItem(LAST_VISIT_KEY);
    if (prev) timeSince = Date.now() - Number(prev);
    localStorage.setItem(LAST_VISIT_KEY, String(Date.now()));
  } catch { /* ignore */ }
  trackAppEvent(
    "app_route_viewed",
    {
      authenticated: !!ctx.userId,
      time_since_last_visit_ms: timeSince,
    },
    { ...ctx, route },
  );
}

export function trackCta(name: string, props: Record<string, unknown> = {}, ctx: Ctx = {}) {
  trackAppEvent("cta_clicked", { name, ...props }, ctx);
}

export function trackTimeOnRoute(route: string, durationMs: number, ctx: Ctx = {}) {
  if (durationMs < 250) return; // ignore noise
  trackAppEvent("time_on_route", { duration_ms: durationMs }, { ...ctx, route });
}

export function trackSessionExpired(fromRoute: string) {
  trackAppEvent("session_expired_redirect", { from_route: fromRoute });
}
